import Button from 'tailwind-components/Button/Button';
import PropTypes from 'prop-types';
import React from 'react';
import CheckBoxItemMemoized from './CheckBoxItem';
import styles from '../AgencySettings.module.scss';
import { Save } from 'lucide-react';
import { getCompany } from 'helpers/getCompanyVariables';
import { FormGroup, Stack, TextField } from '@mui/material';

const company = getCompany();
const isOneill = company === 'oneill';
const isSunshine = company === 'sunshine';

/**
 * carrier-type visibility preferences form for the agency onboarding settings.
 * @param preferencesData - current preference values
 * @param handleChangePreference - toggles a boolean checkbox preference (stable)
 * @param handleChangeAgencyCodePreference - updates the HealthSherpa code field
 * @param handleSavePreferences - persists the preferences
 * @param isSaveDisabled - whether the save button is disabled
 * @param isUpdatingPreferences - whether a save is in flight
 */
export default function FormPreferencesContent({
  preferencesData,
  handleChangePreference,
  handleChangeAgencyCodePreference,
  handleSavePreferences,
  isSaveDisabled,
  isUpdatingPreferences
}) {
  return (
    <>
      <Stack>
        <p className='font-semibold mt-0'>HealthSherpa Agency Code:</p>
        <TextField
          id='Healthsherpa_Agency_Code'
          label='Type HealthSherpa agency code'
          variant='outlined'
          value={preferencesData.Healthsherpa_Agency_Code}
          onChange={e =>
            handleChangeAgencyCodePreference(e, 'Healthsherpa_Agency_Code')
          }
        />
      </Stack>
      <p className='font-semibold mb-0 mt-6'>
        Select from the options below to hide carrier types from the{' '}
        <a
          className='hover:underline!'
          href='/carrier-appointments'
          target='_blank'
          rel='noopener noreferrer'
        >
          Carrier Appointments
        </a>{' '}
        section for agents in this agency:
      </p>
      <div className={styles.preferencesContainer}>
        <FormGroup className={styles.preferences}>
          {isSunshine && (
            <>
              <CheckBoxItemMemoized
                checked={preferencesData.Hide_ACA}
                label='Hide ACA Major'
                preference='Hide_ACA'
                onChange={handleChangePreference}
              />
              <CheckBoxItemMemoized
                checked={preferencesData.Hide_ACA_Other}
                label='Hide ACA All others'
                preference='Hide_ACA_Other'
                onChange={handleChangePreference}
              />
              <CheckBoxItemMemoized
                checked={preferencesData.Hide_Medicare}
                label='Hide Medicare'
                preference='Hide_Medicare'
                onChange={handleChangePreference}
              />
            </>
          )}
          <CheckBoxItemMemoized
            checked={preferencesData.Hide_OffMarket}
            label={isOneill ? 'Disable Off-Market' : 'Hide Off-Market'}
            preference='Hide_OffMarket'
            onChange={handleChangePreference}
          />
          {isOneill && (
            <CheckBoxItemMemoized
              checked={preferencesData.Hide_Ancillary}
              label='Disable Ancillary'
              preference='Hide_Ancillary'
              onChange={handleChangePreference}
            />
          )}
          {isSunshine && (
            <>
              <CheckBoxItemMemoized
                checked={preferencesData.Hide_Group}
                label='Hide Group'
                preference='Hide_Group'
                onChange={handleChangePreference}
              />
              <CheckBoxItemMemoized
                checked={preferencesData.Hide_Life}
                label='Hide Life'
                preference='Hide_Life'
                onChange={handleChangePreference}
              />
            </>
          )}
        </FormGroup>
        <br />
        <Button
          size='medium'
          text='Save preferences'
          variant='primaryDarkened'
          rounded='small'
          icon={<Save size={20} />}
          onClick={handleSavePreferences}
          isLoading={isUpdatingPreferences}
          disabled={isSaveDisabled || isUpdatingPreferences}
        />
      </div>
    </>
  );
}

FormPreferencesContent.propTypes = {
  preferencesData: PropTypes.object.isRequired,
  handleChangePreference: PropTypes.func.isRequired,
  handleChangeAgencyCodePreference: PropTypes.func.isRequired,
  handleSavePreferences: PropTypes.func.isRequired,
  isSaveDisabled: PropTypes.bool.isRequired,
  isUpdatingPreferences: PropTypes.bool.isRequired
};
